import { z } from 'zod';
import { effectivePaidAmountOf } from '@progear/finance-core';
import {
  cashFlowEntrySchema,
  type CashFlowEntry,
  type CashFlowRequestOptions,
} from '@/lib/cashFlow';

const cents = z.number().int().safe();
export const loanInvoicePartSchema = z.enum(['principal', 'interest', 'fee']);
export type LoanInvoicePart = z.infer<typeof loanInvoicePartSchema>;

const allocationSchema = z.object({
  allocationId: z.string(),
  kind: loanInvoicePartSchema,
  componentEntryId: z.string(),
  amountCents: cents.positive(),
});
export type LoanInvoiceAllocation = z.infer<typeof allocationSchema>;

const linkedInvoiceSchema = z.object({
  linkId: z.string(),
  entryId: z.string(),
  idempotencyKey: z.string(),
  linkedAt: z.string(),
  invoiceParty: z.string(),
  invoiceDocNumber: z.string().nullable(),
  invoiceGrossCents: cents.positive(),
  canUnlink: z.boolean(),
  unlinkBlockedReason: z.string().nullable(),
  allocations: z.array(allocationSchema).min(1),
  entry: cashFlowEntrySchema.nullable(),
});
export type LinkedLoanInvoice = z.infer<typeof linkedInvoiceSchema>;

const linkedInvoicesEnvelopeSchema = z.object({
  loanId: z.string(),
  seq: z.number().int().positive(),
  linkedInvoices: z.array(linkedInvoiceSchema),
});
export type LinkedLoanInvoices = z.infer<typeof linkedInvoicesEnvelopeSchema>;

const capacityPartSchema = z.object({
  grossCents: cents.nonnegative(),
  allocatedCents: cents.nonnegative(),
  remainingCents: cents.nonnegative(),
});

export const loanInvoiceCapacitySchema = z.object({
  principal: capacityPartSchema,
  interest: capacityPartSchema,
  fee: capacityPartSchema,
});
export type LoanInvoiceCapacity = z.infer<typeof loanInvoiceCapacitySchema>;

export const loanInvoiceAllocationInputSchema = z.object({
  principalCents: cents.nonnegative(),
  interestCents: cents.nonnegative(),
  feeCents: cents.nonnegative(),
});
export type LoanInvoiceAllocationInput = z.infer<typeof loanInvoiceAllocationInputSchema>;

const installmentInvoiceCandidateSchema = z.object({
  entryId: z.string(),
  entry: cashFlowEntrySchema,
  grossCents: cents.positive(),
  proposedAllocation: loanInvoiceAllocationInputSchema,
  exactMatch: z.boolean(),
  reasons: z.array(z.string()),
});
export type InstallmentInvoiceCandidate = z.infer<typeof installmentInvoiceCandidateSchema>;

const installmentInvoiceCandidatesEnvelopeSchema = z.object({
  loanId: z.string(),
  seq: z.number().int().positive(),
  installmentDueDate: z.string(),
  capacity: loanInvoiceCapacitySchema,
  totalRemainingCapacityCents: cents.nonnegative(),
  candidates: z.array(installmentInvoiceCandidateSchema),
});
export type InstallmentInvoiceCandidates = z.infer<typeof installmentInvoiceCandidatesEnvelopeSchema>;

const nullableComponentEntryIdsSchema = z.object({
  principal: z.string().nullable(),
  interest: z.string().nullable(),
  fee: z.string().nullable(),
});

const invoiceLinkContextCandidateSchema = z.object({
  loanId: z.string(),
  seq: z.number().int().positive(),
  lender: z.string(),
  installmentDueDate: z.string(),
  loanEntryId: z.string().nullable(),
  componentEntryIds: nullableComponentEntryIdsSchema,
  capacity: z.object({
    principal: z.object({ grossCents: cents.nonnegative(), remainingCents: cents.nonnegative() }),
    interest: z.object({ grossCents: cents.nonnegative(), remainingCents: cents.nonnegative() }),
    fee: z.object({ grossCents: cents.nonnegative(), remainingCents: cents.nonnegative() }),
  }),
  totalRemainingCapacityCents: cents.nonnegative(),
  proposedAllocation: loanInvoiceAllocationInputSchema,
  exactMatch: z.boolean(),
  reasons: z.array(z.string()),
});

const invoiceLinkContextSchema = z.object({
  entryId: z.string(),
  invoice: z.object({
    entryId: z.string(),
    party: z.string(),
    docNumber: z.string().nullable(),
    grossCents: cents,
    expectedDate: z.string(),
  }),
  relation: z.object({
    linkId: z.string(),
    loanId: z.string(),
    seq: z.number().int().positive(),
    lender: z.string().nullable(),
    installmentDueDate: z.string().nullable(),
    loanEntryId: z.string().nullable(),
    componentEntryIds: nullableComponentEntryIdsSchema,
    allocation: loanInvoiceAllocationInputSchema,
    canUnlink: z.boolean(),
    unlinkBlockedReason: z.string().nullable(),
  }).nullable(),
  candidates: z.array(invoiceLinkContextCandidateSchema),
  entry: cashFlowEntrySchema,
});
export type InvoiceLoanLinkContext = z.infer<typeof invoiceLinkContextSchema>;

const linkEnvelopeSchema = z.object({
  link: z.object({
    linkId: z.string(),
    idempotent: z.boolean(),
    componentIds: z.record(z.string()).optional(),
  }),
});

const unlinkEnvelopeSchema = z.object({
  unlinked: z.literal(true),
  detail: z.object({
    unlinked: z.literal(true),
    linkId: z.string(),
    loanId: z.string(),
    seq: z.number().int().positive(),
  }),
});

export interface LinkLoanInvoiceInput extends LoanInvoiceAllocationInput {
  loanId: string;
  seq: number;
  entryId: string;
  idempotencyKey: string;
}

function authHeaders(
  accessToken: string | undefined,
  base: Record<string, string> = {},
): Record<string, string> {
  return accessToken ? { ...base, Authorization: `Bearer ${accessToken}` } : base;
}

async function requestError(prefix: string, response: Response): Promise<Error> {
  const body = await response.json().catch(() => null) as { error?: { message?: string }; message?: string } | null;
  const detail = body?.error?.message ?? body?.message;
  return new Error(detail ? `${prefix}: ${detail}` : `${prefix}: ${response.status}`);
}

export async function fetchLinkedLoanInvoices(
  loanId: string,
  seq: number,
  options: CashFlowRequestOptions = {},
): Promise<LinkedLoanInvoices> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const response = await fetchImpl(`${baseUrl}/api/loans/${loanId}/installments/${seq}/invoices`, {
    headers: authHeaders(accessToken),
  });
  if (!response.ok) throw await requestError('Laenumakse seoste laadimine ebaõnnestus', response);
  return linkedInvoicesEnvelopeSchema.parse(await response.json());
}

export async function fetchInstallmentInvoiceCandidates(
  loanId: string,
  seq: number,
  options: CashFlowRequestOptions = {},
): Promise<InstallmentInvoiceCandidates> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const response = await fetchImpl(
    `${baseUrl}/api/loans/${loanId}/installments/${seq}/invoice-candidates`,
    { headers: authHeaders(accessToken) },
  );
  if (!response.ok) throw await requestError('Arvekandidaatide laadimine ebaõnnestus', response);
  return installmentInvoiceCandidatesEnvelopeSchema.parse(await response.json());
}

export async function fetchInvoiceLoanLinkContext(
  entryId: string,
  options: CashFlowRequestOptions = {},
): Promise<InvoiceLoanLinkContext> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const response = await fetchImpl(`${baseUrl}/api/loans/invoice-link-context/${entryId}`, {
    headers: authHeaders(accessToken),
  });
  if (!response.ok) throw await requestError('Laenumaksete laadimine ebaõnnestus', response);
  return invoiceLinkContextSchema.parse(await response.json());
}

export async function linkLoanInvoice(
  input: LinkLoanInvoiceInput,
  options: CashFlowRequestOptions = {},
): Promise<z.infer<typeof linkEnvelopeSchema>> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const response = await fetchImpl(
    `${baseUrl}/api/loans/${input.loanId}/installments/${input.seq}/invoices`,
    {
      method: 'POST',
      headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) throw await requestError('Arve sidumine laenumaksega ebaõnnestus', response);
  return linkEnvelopeSchema.parse(await response.json());
}

export async function unlinkLoanInvoice(
  input: { loanId: string; seq: number; entryId: string },
  options: CashFlowRequestOptions = {},
): Promise<z.infer<typeof unlinkEnvelopeSchema>> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const response = await fetchImpl(
    `${baseUrl}/api/loans/${input.loanId}/installments/${input.seq}/invoices/${input.entryId}`,
    {
      method: 'DELETE',
      headers: authHeaders(accessToken),
    },
  );
  if (!response.ok) throw await requestError('Laenumakse seose eemaldamine ebaõnnestus', response);
  return unlinkEnvelopeSchema.parse(await response.json());
}

export interface NormalizedLoanInvoiceRelation {
  linkId: string;
  loanId: string;
  seq: number;
  lender: string;
  dueDate: string;
  loanEntryId: string;
  invoiceEntryId: string;
  invoiceDocNumber: string | null;
  invoiceParty: string;
  invoiceGrossCents: number;
  invoiceSettledCents: number;
  principalCents: number;
  interestCents: number;
  feeCents: number;
  canUnlink: boolean;
  unlinkBlockedReason: string | null;
}

export interface NormalizedLoanInvoiceCandidate {
  id: string;
  loanId: string;
  seq: number;
  lender: string;
  dueDate: string;
  loanEntryId: string;
  invoiceEntryId: string;
  invoiceDocNumber: string | null;
  invoiceParty: string;
  invoiceGrossCents: number;
  invoiceSettledCents: number;
  availablePrincipalCents: number;
  availableInterestCents: number;
  availableFeeCents: number;
  proposedPrincipalCents: number;
  proposedInterestCents: number;
  proposedFeeCents: number;
  exactMatch: boolean;
  reasons: string[];
}

export function allocationCentsOf(
  allocations: readonly LoanInvoiceAllocation[],
  kind: LoanInvoicePart,
): number {
  return allocations.find((allocation) => allocation.kind === kind)?.amountCents ?? 0;
}

export function componentEntryIdOf(
  allocations: readonly LoanInvoiceAllocation[],
  preferred: LoanInvoicePart = 'principal',
): string {
  return (
    allocations.find((allocation) => allocation.kind === preferred)
    ?? allocations.find((allocation) => allocation.kind === 'interest')
    ?? allocations[0]
  ).componentEntryId;
}

export function effectiveEntrySettlement(entry: CashFlowEntry | null): number {
  if (entry === null) return 0;
  return effectivePaidAmountOf(entry) + entry.adjustedCents;
}